import { useState, useCallback, useEffect, useRef } from 'react'
import storage from '@/utils/storage'
import preferencesApi from '@/utils/api/preferencesApi'
import { preferredLinesKey, defaultLineKey } from '@/utils/preferenceKeys'
import type { LineOption } from '@/utils/storage'

type RowItem = { id: string; value: string }

interface UseProductionLinesOptions {
    /** The space whose preferred lines are edited */
    spaceId: string
    endpoint: string
    getApiToken: () => Promise<string>
    preferencesApiEnabled: boolean
}

interface UseProductionLinesResult {
    rows: RowItem[]
    setRows: React.Dispatch<React.SetStateAction<RowItem[]>>
    isSaving: boolean
    /** True for a moment after a successful save */
    isSaved: boolean
    saveError: string | null
    handleChange: (selectedLines: LineOption[]) => Promise<void>
    getPreferredLinesFromApi: () => Promise<void>
    clearSaveError: () => void
}

const SAVED_VISIBLE_MS = 2500

const savedLinesToRows = (lines: LineOption[]): RowItem[] => {
    if (lines.length === 0) return [{ id: 'row-0', value: '' }]
    return lines.map((line, i) => ({ id: `row-${i}`, value: line.value }))
}

export function useProductionLines({
    spaceId,
    endpoint,
    getApiToken,
    preferencesApiEnabled,
}: UseProductionLinesOptions): UseProductionLinesResult {
    const [rows, setRows] = useState<RowItem[]>(() => savedLinesToRows(storage.getPreferredLines(spaceId)))
    const [isSaving, setIsSaving] = useState(false)
    const [isSaved, setIsSaved] = useState(false)
    const savedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    useEffect(() => () => clearTimeout(savedTimer.current), [])
    const [saveError, setSaveError] = useState<string | null>(null)

    const getPreferredLinesFromApi = useCallback(async () => {
        if (!preferencesApiEnabled || !endpoint || !spaceId) return
        try {
            const token = await getApiToken()
            const preference = await preferencesApi.getPreferences(endpoint, token)

            const preferred = preference[preferredLinesKey(spaceId)]
            const preferredLines = Array.isArray(preferred) ? preferred : []
            setRows(savedLinesToRows(preferredLines.map((line: string) => ({ value: line, label: line }))))
        } catch {
            // Handle error if needed
        }
    }, [spaceId, preferencesApiEnabled, endpoint, getApiToken])

    const handleChange = useCallback(
        async (selectedLines: LineOption[]) => {
            // The default line is always the first preferred line (None when there are none)
            const defaultLine = selectedLines[0]?.value ?? null

            // Always persist to localStorage immediately for offline/fast-path.
            storage.setPreferredLines(selectedLines, spaceId)
            storage.setDefaultLine(defaultLine, spaceId)

            if (!preferencesApiEnabled || !endpoint || !spaceId) return

            clearTimeout(savedTimer.current)
            setIsSaved(false)
            setSaveError(null)
            setIsSaving(true)
            try {
                const token = await getApiToken()
                await preferencesApi.patchPreferences(endpoint, token, {
                    [preferredLinesKey(spaceId)]: selectedLines.map((l) => l.value),
                    [defaultLineKey(spaceId)]: defaultLine,
                })
                setIsSaved(true)
                savedTimer.current = setTimeout(() => setIsSaved(false), SAVED_VISIBLE_MS)
            } catch {
                setSaveError('error')
            } finally {
                setIsSaving(false)
            }
        },
        [spaceId, endpoint, getApiToken, preferencesApiEnabled]
    )

    const clearSaveError = useCallback(() => setSaveError(null), [])

    return { rows, setRows, isSaving, isSaved, saveError, handleChange, getPreferredLinesFromApi, clearSaveError }
}
